/**
 * §5 terminal / hold routes — the calm walls.
 *
 * Thin wrappers over the presentational TerminalPage that supply the candidate name from the
 * resolved session (StepRoute has already gated each of these to its matching status). NotFound
 * stands alone — it renders when there is no valid token to resolve.
 *
 * **These are calm pages, not error pages, and that is a product decision rather than a tone
 * preference.** The referee is a stranger who agreed to do someone a favour; every one of these
 * states is something that happened TO them (a link aged out, the candidate changed their mind,
 * a moderation pass wants a human look). None of them is their fault and none of them is
 * recoverable by trying harder, so none of them gets an alarm, a retry, or a 404. One sentence,
 * and an honest account of what happens next.
 *
 * **Already-signed is deliberately NOT here.** It is a face of `Done` (`routes/Done.tsx`): the
 * step model maps `signed` → `done`, and the mock's edge state C is what that route shows when the
 * visit did not produce the signature. Re-opening a signed link lands on the confirmation, not on
 * a terminal wall — "the link resolves calmly forever, never a 404".
 */
import { TerminalPage } from '@/components/TerminalPage';
import { useSession } from '@/session/useSession';

export function DeclinedRoute() {
  const { context } = useSession();
  return <TerminalPage kind="declined" candidateName={context?.candidate.name} />;
}

/**
 * The mock's "Send me a new link" button is NOT built — see the reasoning in `TerminalPage`'s
 * `expired` copy. There is no referee-facing resend verb in the contract (the only resend is
 * candidate-authenticated), so the page tells them the one thing that actually works instead of
 * offering a control wired to `() => {}`.
 */
export function ExpiredRoute() {
  const { context } = useSession();
  return <TerminalPage kind="expired" candidateName={context?.candidate.name} />;
}

export function WithdrawnRoute() {
  const { context } = useSession();
  return <TerminalPage kind="withdrawn" candidateName={context?.candidate.name} />;
}

export function UnderReviewRoute() {
  const { context } = useSession();
  return <TerminalPage kind="under-review" candidateName={context?.candidate.name} />;
}

export function NotFoundRoute() {
  return <TerminalPage kind="not-found" />;
}
